// src/pages/EditProject.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AdminLayout from '../components/layout/AdminLayout';
import ProjectForm from '../components/forms/ProjectForm';
import { getProjectById, updateProject } from '../services/ProjectService';
import { deleteImages } from '../services/UploadService';
import { IProject } from '../types/Project';
import { useToast } from '../hooks/useToast'; // ✅ Importar toast
import styles from '../styles/FormPages.module.css';

const DEFAULT_PLACEHOLDER_IMAGE = 'https://via.placeholder.com/1200x800.png?text=Proyecto+Paso';

const EditProject: React.FC = () => {
    const { id } = useParams<{ id: string }>(); 
    const navigate = useNavigate();
    const [project, setProject] = useState<IProject | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const hasSaved = useRef(false);
    const sessionUploads = useRef<string[]>([]);
    const toast = useToast(); // ✅ Usar toast

    useEffect(() => {
        const uploadsToClean = sessionUploads;
        const cleanupFunction = deleteImages;
        return () => {
            if (!hasSaved.current && uploadsToClean.current.length > 0) {
                console.log("Cancelando edición, limpiando imágenes:", uploadsToClean.current);
                cleanupFunction(uploadsToClean.current);
            }
        };
    }, []);

    useEffect(() => {
        setLoading(true);
        setError(null);
        const projectId = id ? parseInt(id, 10) : undefined;
        
        const fetchProject = async () => {
            if (projectId !== undefined && !isNaN(projectId)) {
                try {
                    const fetchedProject = await getProjectById(projectId); 
                    setProject(fetchedProject);
                } catch (err: any) { 
                    setError(err.message);
                    toast.error('Error', 'No se pudo cargar el proyecto.');
                }
            } else { 
                setError('ID de proyecto inválido.');
                toast.error('Error', 'ID de proyecto inválido.');
            }
            setLoading(false);
        };
        fetchProject();
    }, [id]);

    const handleSubmit = async (formData: IProject, imagesToDelete: string[]) => {
        try {
            let finalImageUrl = formData.imageUrl;
            if (!finalImageUrl) {
                if (formData.gallery.length > 0) {
                    finalImageUrl = formData.gallery[0].url;
                } else {
                    finalImageUrl = DEFAULT_PLACEHOLDER_IMAGE;
                }
            }

            const dataToSubmit = {
                ...formData,
                imageUrl: finalImageUrl,
                gallery: formData.gallery 
            };

            await updateProject(dataToSubmit);
            
            if (imagesToDelete.length > 0) {
                await deleteImages(imagesToDelete);
            }
            
            hasSaved.current = true;
            toast.success(
                'Proyecto actualizado',
                `"${formData.title}" ha sido actualizado exitosamente.`
            );
            navigate('/projects');
        } catch (error: any) {
            toast.error(
                'Error al actualizar proyecto',
                error.message || 'No se pudo actualizar el proyecto.'
            );
        }
    };

    if (loading) { 
        return ( 
            <AdminLayout pageTitle="Cargando...">
                <div className={styles.loadingMessage}>Cargando...</div>
            </AdminLayout> 
        ); 
    }
    
    if (error) { 
        return ( 
            <AdminLayout pageTitle="Error">
                <div className={styles.loadingMessage} style={{ color: 'red' }}>Error: {error}</div>
            </AdminLayout> 
        ); 
    }
    
    if (!project) { 
        return ( 
            <AdminLayout pageTitle="Error">
                <div className={styles.loadingMessage}>Proyecto no encontrado.</div>
            </AdminLayout> 
        ); 
    }

    return (
        <AdminLayout pageTitle={`Editar Proyecto: ${project.title}`}>
            <div className={styles.formWrapper}>
                <div className={styles.pageHeader}>
                    <h2 className={styles.title}>{`Editar Proyecto: ${project.title}`}</h2>
                    <Link to="/projects" className={styles.backButton}>← Volver</Link>
                </div>
                <ProjectForm 
                    initialData={project} 
                    onSubmit={handleSubmit} 
                    isEditing={true} 
                    sessionUploadsRef={sessionUploads}
                />
            </div>
        </AdminLayout>
    );
};

export default EditProject;